import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Shield, Users, BookOpen, BarChart3, Brain, Award, CheckCircle, Compass, FileSearch } from "lucide-react";
import Layout from "@/components/layout/Layout";
import SectionHeading from "@/components/ui/SectionHeading";
import ServiceCard from "@/components/ui/ServiceCard";
import { HeroCTAGroup, ServiceCTAGroup } from "@/components/ui/CTA";
import { CertificationFunnel } from "@/components/ui/ConversionFunnel";

const stats = [
  { value: "10+", label: "Years Experience" },
  { value: "150+", label: "Organizations Served" },
  { value: "500+", label: "Certifications Delivered" },
  { value: "50+", label: "CSR Projects" },
];

const services = [
  { icon: Shield, title: "ISO Certifications", description: "Explore ISO 9001, 14001, 45001, 22000, 27001, 13485 and integrated management system pathways.", path: "/services/iso-certifications" },
  { icon: Users, title: "CSR & Impact Assessment", description: "Understand CSR readiness, project documentation, monitoring, impact assessment and compliance reporting pathways.", path: "/services/csr-impact" },
  { icon: BookOpen, title: "Skill Development", description: "Review RPL, corporate training, government skill initiatives and competency mapping support options.", path: "/services/skill-development" },
  { icon: BarChart3, title: "Tender & Procurement Readiness", description: "Explore tender documentation, vendor readiness, compliance checklists and procurement preparedness pathways.", path: "/services/research-surveys" },
  { icon: Award, title: "Management Readiness", description: "Map process, SOP, documentation and organizational readiness needs before moving into guided support.", path: "/services/management-consulting" },
];

const aiFeatures = [
  { title: "AI Compliance Advisor", description: "Get educational certification and compliance pathway suggestions based on your industry and operations.", path: "/tools/compliance-advisor" },
  { title: "Certification Readiness Checker", description: "Assess documentation and process readiness with an AI-guided evaluation.", path: "/tools/readiness-checker" },
  { title: "CSR Project Idea Generator", description: "Explore CSR project ideas and documentation inputs aligned with Schedule VII themes.", path: "/tools/csr-generator" },
  { title: "Impact Assessment Estimator", description: "Estimate survey scope, methodology and evidence needs for impact assessment planning.", path: "/tools/impact-estimator" },
];

const GETSAARTHI_HANDOFF_URL = "https://getsaarthi.com/route/platform/PLT-002?action=enquiry&source=sigmacert&origin=sigmacert_homepage&context=certification_compliance_tender_readiness&cta=continue_with_getsaarthi&intent=certification_compliance_support";

const aiGuides = [
  {
    name: "Meera Sethi - Certification & Compliance Guide",
    focus: "Helps users understand certification pathways, compliance readiness signals, and documentation gaps.",
    icon: Compass,
  },
  {
    name: "Kabir Malhotra - Tender & Procurement Readiness Guide",
    focus: "Helps users explore tender documentation, vendor readiness, and procurement preparedness pathways.",
    icon: FileSearch,
  },
];

export default function HomePage() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 25% 50%, hsl(42 85% 55% / 0.3) 0%, transparent 50%), radial-gradient(circle at 75% 30%, hsl(175 50% 40% / 0.2) 0%, transparent 50%)" }} />
        </div>
        <div className="container-narrow relative section-padding py-24 md:py-32 lg:py-40">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 inline-block rounded-full border border-accent/30 bg-accent/10 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-accent"
            >
              AI-Enabled Consulting Platform
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-6 font-heading text-4xl font-bold leading-tight text-primary-foreground md:text-5xl lg:text-6xl"
            >
              Certification, Compliance &{" "}
              <span className="gradient-text">Impact Consulting</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-8 max-w-xl text-lg leading-relaxed text-primary-foreground/75"
            >
              Explore certification, compliance, and tender-readiness pathways with guided support through GetSaarthi.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <HeroCTAGroup />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-border bg-card">
        <div className="container-narrow grid grid-cols-2 gap-6 px-4 py-12 md:grid-cols-4 md:px-8">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <div className="font-heading text-3xl font-bold text-accent md:text-4xl">{stat.value}</div>
              <div className="mt-1 text-sm text-muted-foreground">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="section-padding">
        <div className="container-narrow">
          <SectionHeading tag="Discovery Pathways" title="Certification, Compliance & Tender Readiness" description="Educational discovery routes for MSMEs, founders, consultants, vendors, and institutions preparing for certification, documentation, and procurement requirements." />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <ServiceCard key={s.path} {...s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* AI Tools */}
      <section className="section-padding bg-primary">
        <div className="container-narrow">
          <SectionHeading
            tag="AI-Powered Tools"
            title="Smart Consulting Advisory"
            description="Use AI-guided discovery to understand certification, compliance, CSR and tender-readiness requirements before choosing the next support path."
          />
          <div className="grid gap-6 sm:grid-cols-2">
            {aiFeatures.map((tool, i) => (
              <motion.div
                key={tool.path}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link
                  to={tool.path}
                  className="group flex h-full flex-col rounded-xl border border-primary-foreground/15 bg-primary-foreground/5 p-6 backdrop-blur transition-all hover:border-accent hover:bg-primary-foreground/10"
                >
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                    <Brain className="h-5 w-5" />
                  </div>
                  <h3 className="mb-2 font-heading text-lg font-semibold text-primary-foreground">{tool.title}</h3>
                  <p className="flex-1 text-sm text-primary-foreground/70">{tool.description}</p>
                  <span className="mt-4 flex items-center gap-1 text-sm font-medium text-accent">
                    Try now <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Guides */}
      <section className="section-padding">
        <div className="container-narrow">
          <SectionHeading
            tag="AI Service Guides"
            title="Guided Discovery, Not Official Advice"
            description="AI Service Guide - Educational and routing support."
          />
          <div className="grid gap-6 md:grid-cols-2">
            {aiGuides.map((guide, i) => (
              <motion.div
                key={guide.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-xl border border-border bg-card p-6"
              >
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-secondary text-accent">
                  <guide.icon className="h-5 w-5" />
                </div>
                <h3 className="mb-2 font-heading text-lg font-semibold text-card-foreground">{guide.name}</h3>
                <p className="text-sm text-muted-foreground">{guide.focus}</p>
                <p className="mt-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  AI Service Guide - Educational and routing support.
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Conversion Funnel */}
      <section className="section-padding bg-secondary/20">
        <div className="container-narrow">
          <CertificationFunnel />
        </div>
      </section>

      {/* Why SigmaCert */}
      <section className="section-padding">
        <div className="container-narrow">
          <SectionHeading tag="Why SigmaCert" title="Trusted by 150+ Organizations" />
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { title: "Pathway Clarity", desc: "Practical discovery support for certification, compliance, CSR and tender-readiness requirements." },
              { title: "AI-Driven Readiness", desc: "AI tools help you understand likely requirements, documentation gaps, and next-step routing." },
              { title: "GetSaarthi Handoff", desc: "Need guided execution support? Continue with GetSaarthi." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-xl border border-border bg-card p-6"
              >
                <CheckCircle className="mb-3 h-8 w-8 text-accent" />
                <h3 className="mb-2 font-heading text-lg font-semibold text-card-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-accent">
        <div className="container-narrow text-center">
          <h2 className="mb-4 font-heading text-3xl font-bold text-accent-foreground">Ready to Map Your Next Step?</h2>
          <p className="mx-auto mb-8 max-w-xl text-accent-foreground/80">
            Use the free AI Compliance Advisor to explore likely requirements, then continue with GetSaarthi when you need guided execution support.
          </p>
          <ServiceCTAGroup />
          <a
            href={GETSAARTHI_HANDOFF_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg border border-accent-foreground/30 px-6 py-3 font-heading text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent-foreground/10"
          >
            Continue with GetSaarthi
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </Layout>
  );
}
